import { createRoutine } from 'redux-saga-routines'
import { call, put, takeLatest, takeEvery, delay, select, race, take, fork, cancel } from '@redux-saga/core/effects'
import { toast } from 'react-toastify'
import * as reportingService from 'services/reportingService'
import { getApiErrors } from 'utils/errors'
import { selectIsPolling, selectCurrentReport } from './selectors'

// Action creators
export const checkReportingAccessRoutine = createRoutine('REPORTING/CHECK_ACCESS')
export const triggerReportingReportRoutine = createRoutine('REPORTING/TRIGGER_REPORT')
export const getReportingReportRoutine = createRoutine('REPORTING/GET_REPORT')
export const getReportingReportsRoutine = createRoutine('REPORTING/GET_REPORTS')
export const pollReportingReportRoutine = createRoutine('REPORTING/POLL_REPORT')
export const stopPollingReportingRoutine = createRoutine('REPORTING/STOP_POLLING')
export const clearReportingRoutine = createRoutine('REPORTING/CLEAR')
export const updateReportingAnswerRoutine = createRoutine('REPORTING/UPDATE_ANSWER')
export const regenerateReportingAnswerRoutine = createRoutine('REPORTING/REGENERATE_ANSWER')
export const cloneReportingReportRoutine = createRoutine('REPORTING/CLONE_REPORT')
export const exportReportingPdfRoutine = createRoutine('REPORTING/EXPORT_PDF')
export const exportReportingWordRoutine = createRoutine('REPORTING/EXPORT_WORD')
export const exportReportingCsvRoutine = createRoutine('REPORTING/EXPORT_CSV')
export const updateReportingParticularsRoutine = createRoutine('REPORTING/UPDATE_PARTICULARS')

// Polling configuration
const POLL_INTERVAL = 3000 // 3 seconds
const MAX_POLL_ATTEMPTS = 200 // 10 minutes max

// Saga functions
function* checkReportingAccess({ payload }) {
  const { bpId } = payload
  yield put(checkReportingAccessRoutine.request())
  try {
    const result = yield call(reportingService.checkAccess, bpId)
    yield put(checkReportingAccessRoutine.success(result.data.data))
  } catch (error) {
    yield put(checkReportingAccessRoutine.failure(getApiErrors(error)))
  } finally {
    yield put(checkReportingAccessRoutine.fulfill())
  }
}

function* triggerReportingReport({ payload }) {
  const { bpId, reportType } = payload
  yield put(triggerReportingReportRoutine.request())
  try {
    const result = yield call(reportingService.triggerReport, bpId, reportType || 'insurance')
    const newReport = result.data.data
    yield put(triggerReportingReportRoutine.success(newReport))

    if (newReport.isCompleted) {
      // Report completed synchronously (sync queue) — no polling needed
      toast.success('Report is ready!')
      // Load the full report with answers
      yield put(getReportingReportRoutine.trigger({ bpId, reportId: newReport.id }))
    } else {
      // Report is processing async — start polling
      toast.success('Report generation started')
      yield put(pollReportingReportRoutine.trigger({ bpId, reportId: newReport.id }))
    }

    // Refresh reports list so the version selector updates
    yield put(getReportingReportsRoutine.trigger({ bpId, reportType }))
  } catch (error) {
    const errorData = getApiErrors(error)
    yield put(triggerReportingReportRoutine.failure(errorData))

    // Handle specific error cases
    if (error.response?.status === 409) {
      toast.info('A report is already being generated. Please wait.')
      // If there's an existing report, start polling for it
      if (error.response?.data?.data?.id) {
        yield put(pollReportingReportRoutine.trigger({
          bpId,
          reportId: error.response.data.data.id
        }))
      }
    } else if (error.response?.status === 403) {
      toast.error('You do not have access to reporting')
    } else {
      toast.error('Failed to start report generation. Checking for pending reports...')
      // With sync queue, the trigger may timeout but the report could still be processing.
      // Refresh the reports list to check.
      yield put(getReportingReportsRoutine.trigger({ bpId, reportType }))
    }
  } finally {
    yield put(triggerReportingReportRoutine.fulfill())
  }
}

function* getReportingReport({ payload }) {
  const { bpId, reportId } = payload
  yield put(getReportingReportRoutine.request())
  try {
    const result = yield call(reportingService.getReport, bpId, reportId)
    yield put(getReportingReportRoutine.success(result.data.data))
  } catch (error) {
    yield put(getReportingReportRoutine.failure(getApiErrors(error)))
    toast.error('Failed to load report')
  } finally {
    yield put(getReportingReportRoutine.fulfill())
  }
}

function* getReportingReports({ payload }) {
  const { bpId, page, perPage, reportType } = payload
  yield put(getReportingReportsRoutine.request())
  try {
    const result = yield call(reportingService.getReports, bpId, { page, perPage, reportType })
    yield put(getReportingReportsRoutine.success({
      reports: result.data.data,
      meta: result.data.meta
    }))
  } catch (error) {
    yield put(getReportingReportsRoutine.failure(getApiErrors(error)))
  } finally {
    yield put(getReportingReportsRoutine.fulfill())
  }
}

function* pollReportingReport({ payload }) {
  const { bpId, reportId } = payload
  yield put(pollReportingReportRoutine.request())

  let attempts = 0

  try {
    while (attempts < MAX_POLL_ATTEMPTS) {
      const isPolling = yield select(selectIsPolling)
      if (!isPolling) {
        break
      }

      const result = yield call(reportingService.getReport, bpId, reportId)
      const report = result.data.data

      yield put(getReportingReportRoutine.success(report))

      // Check if report is complete or failed
      if (report.isCompleted) {
        yield put(pollReportingReportRoutine.success(report))
        toast.success('Report is ready!')

        // Refresh reports list so the version selector shows all versions
        const currentReportType = report.reportType?.slug
        yield put(getReportingReportsRoutine.trigger({ bpId, reportType: currentReportType }))
        break
      } else if (report.isFailed) {
        yield put(pollReportingReportRoutine.failure({
          message: report.errorMessage || 'Report generation failed'
        }))
        toast.error('Report generation failed. Please try again.')
        break
      }

      attempts++
      yield delay(POLL_INTERVAL)
    }

    if (attempts >= MAX_POLL_ATTEMPTS) {
      yield put(pollReportingReportRoutine.failure({
        message: 'Report generation timed out. Please try again.'
      }))
      toast.error('Report generation timed out')
    }
  } catch (error) {
    yield put(pollReportingReportRoutine.failure(getApiErrors(error)))
  } finally {
    yield put(pollReportingReportRoutine.fulfill())
  }
}

function* updateReportingAnswer({ payload }) {
  const { bpId, reportId, questionId, answer } = payload
  yield put(updateReportingAnswerRoutine.request({ questionId }))
  try {
    const result = yield call(reportingService.updateAnswer, bpId, reportId, questionId, answer)
    yield put(updateReportingAnswerRoutine.success(result.data.data))
    toast.success(`Answer updated (v${result.data.data.version})`)
    // Refresh the reports list since a new version was created
    const currentRpt = yield select(selectCurrentReport)
    yield put(getReportingReportsRoutine.trigger({ bpId, reportType: currentRpt?.reportType?.slug }))
  } catch (error) {
    yield put(updateReportingAnswerRoutine.failure(getApiErrors(error)))
    toast.error('Failed to update answer')
  } finally {
    yield put(updateReportingAnswerRoutine.fulfill())
  }
}

function* updateReportingParticulars({ payload }) {
  const { bpId, reportId, particulars } = payload
  yield put(updateReportingParticularsRoutine.request())
  try {
    const result = yield call(reportingService.updateParticulars, bpId, reportId, particulars)
    yield put(updateReportingParticularsRoutine.success(result.data.data))
    toast.success('Particulars updated')
  } catch (error) {
    yield put(updateReportingParticularsRoutine.failure(getApiErrors(error)))
    toast.error('Failed to update particulars')
  } finally {
    yield put(updateReportingParticularsRoutine.fulfill())
  }
}

function* regenerateReportingAnswer({ payload }) {
  const { bpId, reportId, questionId, feedback, customInstruction, subKey } = payload
  yield put(regenerateReportingAnswerRoutine.request({ questionId, subKey }))

  // Helper function to poll for regeneration completion
  // Now polls the NEW report ID (since a new version is created upfront)
  function* pollForRegeneration(newReportId, targetQuestionId) {
    let attempts = 0
    const maxAttempts = 40 // 2 minutes max for single answer
    while (attempts < maxAttempts) {
      yield delay(3000)
      const result = yield call(reportingService.getReport, bpId, newReportId)
      const report = result.data.data

      // Check if regeneration is complete (regeneratingQuestionId cleared from metadata)
      const isStillRegenerating = report.metadata?.regeneratingQuestionId === targetQuestionId
      if (!isStillRegenerating) {
        yield put(regenerateReportingAnswerRoutine.success(report))
        toast.success(`Answer regenerated (v${report.version})`)
        // Refresh the reports list since a new version was created
        yield put(getReportingReportsRoutine.trigger({ bpId, reportType: report.reportType?.slug }))
        return true
      }
      attempts++
    }
    return false
  }

  try {
    // The API now returns a NEW report immediately (new version created upfront)
    const result = yield call(reportingService.regenerateAnswer, bpId, reportId, questionId, {
      feedback,
      customInstruction,
      subKey
    })
    const newReport = result.data.data
    const newReportId = newReport.id

    // Set the new report as current immediately
    yield put(getReportingReportRoutine.success(newReport))
    toast.info(`Regenerating answer (v${newReport.version})...`)

    // Poll the NEW report for regeneration completion
    const completed = yield* pollForRegeneration(newReportId, questionId)
    if (!completed) {
      yield put(regenerateReportingAnswerRoutine.failure({ message: 'Regeneration timed out' }))
      toast.error('Answer regeneration timed out')
    }
  } catch (error) {
    // Handle 409 - regeneration already in progress
    if (error.response?.status === 409) {
      const report = error.response?.data?.data
      const regeneratingQuestionId = report?.metadata?.regeneratingQuestionId

      if (regeneratingQuestionId && report) {
        toast.info('Regeneration already in progress. Waiting for completion...')
        // Update the current report state with the returned data
        yield put(getReportingReportRoutine.success(report))
        // Continue polling for the existing regeneration
        const completed = yield* pollForRegeneration(report.id, regeneratingQuestionId)
        if (!completed) {
          yield put(regenerateReportingAnswerRoutine.failure({ message: 'Regeneration timed out' }))
          toast.error('Answer regeneration timed out')
        }
      } else {
        toast.info(error.response?.data?.message || 'A regeneration is already in progress')
        yield put(regenerateReportingAnswerRoutine.failure(getApiErrors(error)))
      }
    } else {
      yield put(regenerateReportingAnswerRoutine.failure(getApiErrors(error)))
      toast.error('Failed to regenerate answer')
    }
  } finally {
    yield put(regenerateReportingAnswerRoutine.fulfill())
  }
}

function* cloneReportingReport({ payload }) {
  const { bpId, reportId } = payload
  yield put(cloneReportingReportRoutine.request())
  try {
    const result = yield call(reportingService.cloneReport, bpId, reportId)
    yield put(cloneReportingReportRoutine.success(result.data.data))
    toast.success(`Report saved as version ${result.data.data.version}`)
    // Refresh the reports list
    const clonedRpt = yield select(selectCurrentReport)
    yield put(getReportingReportsRoutine.trigger({ bpId, reportType: clonedRpt?.reportType?.slug }))
  } catch (error) {
    yield put(cloneReportingReportRoutine.failure(getApiErrors(error)))
    toast.error('Failed to save as new version')
  } finally {
    yield put(cloneReportingReportRoutine.fulfill())
  }
}

function* exportReportingPdf({ payload }) {
  const { bpId, reportId, buildingName, reportTitle } = payload
  yield put(exportReportingPdfRoutine.request())
  try {
    const result = yield call(reportingService.exportPdf, bpId, reportId)

    // Create blob and download
    const blob = new Blob([result.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const title = (reportTitle || 'Report').replace(/\s+/g, '-')
    link.download = `${title}-${buildingName || 'Report'}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    yield put(exportReportingPdfRoutine.success())
    toast.success('PDF exported successfully')
  } catch (error) {
    yield put(exportReportingPdfRoutine.failure(getApiErrors(error)))
    toast.error('Failed to export PDF')
  } finally {
    yield put(exportReportingPdfRoutine.fulfill())
  }
}

function* exportReportingWord({ payload }) {
  const { bpId, reportId, buildingName, reportTitle } = payload
  yield put(exportReportingWordRoutine.request())
  try {
    const result = yield call(reportingService.exportWord, bpId, reportId)

    const blob = new Blob([result.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const title = (reportTitle || 'Report').replace(/\s+/g, '-')
    link.download = `${title}-${buildingName || 'Report'}.docx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    yield put(exportReportingWordRoutine.success())
    toast.success('Word document exported successfully')
  } catch (error) {
    yield put(exportReportingWordRoutine.failure(getApiErrors(error)))
    toast.error('Failed to export Word document')
  } finally {
    yield put(exportReportingWordRoutine.fulfill())
  }
}

function* exportReportingCsv({ payload }) {
  const { bpId, reportId, buildingName, reportTitle } = payload
  yield put(exportReportingCsvRoutine.request())
  try {
    const result = yield call(reportingService.exportCsv, bpId, reportId)

    // Create blob and download
    const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const title = (reportTitle || 'Report').replace(/\s+/g, '-')
    link.download = `${title}-${buildingName || 'Report'}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    yield put(exportReportingCsvRoutine.success())
    toast.success('CSV exported successfully')
  } catch (error) {
    yield put(exportReportingCsvRoutine.failure(getApiErrors(error)))
    toast.error('Failed to export CSV')
  } finally {
    yield put(exportReportingCsvRoutine.fulfill())
  }
}

// Watcher sagas
function* watchCheckAccess() {
  yield takeLatest(checkReportingAccessRoutine.TRIGGER, checkReportingAccess)
}

function* watchTriggerReport() {
  yield takeLatest(triggerReportingReportRoutine.TRIGGER, triggerReportingReport)
}

function* watchGetReport() {
  yield takeLatest(getReportingReportRoutine.TRIGGER, getReportingReport)
}

function* watchGetReports() {
  yield takeLatest(getReportingReportsRoutine.TRIGGER, getReportingReports)
}

function* watchPollReport() {
  let pollTask = null
  while (true) {
    const action = yield take([
      pollReportingReportRoutine.TRIGGER,
      stopPollingReportingRoutine.TRIGGER,
      clearReportingRoutine.TRIGGER
    ])

    // Cancel any running poll task
    if (pollTask) {
      yield cancel(pollTask)
      pollTask = null
    }

    if (action.type === pollReportingReportRoutine.TRIGGER) {
      pollTask = yield fork(pollReportingReport, action)
    } else {
      // Stop or clear — just ensure isPolling is false
      yield put(stopPollingReportingRoutine.success())
    }
  }
}

function* watchUpdateAnswer() {
  // takeEvery (not takeLatest) — sub-enquiry saves can run in rapid succession;
  // cancelling the first one would leave isUpdatingAnswer stuck true forever.
  yield takeEvery(updateReportingAnswerRoutine.TRIGGER, updateReportingAnswer)
}

function* watchUpdateParticulars() {
  yield takeLatest(updateReportingParticularsRoutine.TRIGGER, updateReportingParticulars)
}

function* watchRegenerateAnswer() {
  yield takeLatest(regenerateReportingAnswerRoutine.TRIGGER, regenerateReportingAnswer)
}

function* watchCloneReport() {
  yield takeLatest(cloneReportingReportRoutine.TRIGGER, cloneReportingReport)
}

function* watchExportPdf() {
  yield takeLatest(exportReportingPdfRoutine.TRIGGER, exportReportingPdf)
}

function* watchExportWord() {
  yield takeLatest(exportReportingWordRoutine.TRIGGER, exportReportingWord)
}

function* watchExportCsv() {
  yield takeLatest(exportReportingCsvRoutine.TRIGGER, exportReportingCsv)
}

// Root saga
export const reportingSagas = [
  fork(watchCheckAccess),
  fork(watchTriggerReport),
  fork(watchGetReport),
  fork(watchGetReports),
  fork(watchPollReport),
  fork(watchUpdateAnswer),
  fork(watchUpdateParticulars),
  fork(watchRegenerateAnswer),
  fork(watchCloneReport),
  fork(watchExportPdf),
  fork(watchExportWord),
  fork(watchExportCsv)
]
